import React from 'react';
import { Link } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { Button as RoccoButton } from '@rocco/components/button';
import {
    Filters,
    type FilterSortOption,
} from '@rocco/ui/filter-sort/components/Filters';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { BackButton } from './components/BackButton';
import { useDragScroll } from '../category-page/hooks/useDragScroll';
import { useScrollFade } from '../category-page/hooks/useScrollFade';
import { SortDropdown } from '@rocco/ui/filter-sort/components/SortDropdown';
import type { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { FiltersModal } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { CategoryPills } from '~/ui-rocco/pages/category-page/components/CategoryPills';

interface Breadcrumb {
    href: string;
    label: string;
}

interface ProductsFilterBarProps {
    /** Breadcrumbs for extracting parent category info */
    breadcrumbs: Breadcrumb[];
    /** Search filters from useCategoryFilterSort hook */
    searchFilters: ReturnType<typeof useSearchFilters>;
    /** Sort options from useCategoryFilterSort hook */
    sortOptions: FilterSortOption[];
    /** Whether currently on suppliers endpoint */
    isSuppliersEndpoint: boolean;
    /** Handler for toggling between Products/Brands */
    onToggle: () => void;
    /** Local state for the toggle animation */
    localIsSuppliers: boolean;
    /** Whether to disable highlight transition */
    disableHighlightTransition: boolean;
    /** Setter for localIsSuppliers */
    setLocalIsSuppliers: (value: boolean) => void;
    /** Setter for disableHighlightTransition */
    setDisableHighlightTransition: (value: boolean) => void;
    /** Category pills to show in suppliers mode (replaces filter pills) */
    supplierCategories?: { id: number; title: string; link: string }[];
}

/**
 * Filter bar component for Products page (desktop only).
 * Includes back button, expandable search, filter pills, and Products/Brands toggle.
 */
export const ProductsFilterBar = ({
    breadcrumbs,
    searchFilters,
    sortOptions,
    isSuppliersEndpoint,
    onToggle,
    localIsSuppliers,
    disableHighlightTransition,
    setLocalIsSuppliers,
    setDisableHighlightTransition,
    supplierCategories,
}: ProductsFilterBarProps) => {
    const { scrollRef } = useDragScroll();

    // Track scroll position for dynamic fade masks / grab cursor
    const { showLeftFade, showRightFade, isOverflowing } = useScrollFade(
        scrollRef as React.RefObject<HTMLDivElement | null>,
    );

    // Extract parent category info from breadcrumbs
    // breadcrumbs[length - 2] is the parent category (need at least 2 items to have a parent)
    const parentBreadcrumb =
        breadcrumbs?.length >= 2 ? breadcrumbs[breadcrumbs.length - 2] : null;
    const parentCategoryName = parentBreadcrumb?.label;
    const parentCategoryLink = parentBreadcrumb?.href;
    const shouldShowBackButton = !!parentCategoryLink && !!parentCategoryName;

    return (
        <>
            {/* Row 1: Filter bar - fixed height prevents layout shift, overflow-visible for badges */}
            <div className="flex h-10.5 items-center gap-3 overflow-visible px-4 md:px-12">
                {/* Back Button - only show when not at root */}
                {shouldShowBackButton && parentCategoryName && (
                    <BackButton
                        parentCategoryName={parentCategoryName}
                        parentCategoryLink={parentCategoryLink}
                    />
                )}

                {/* Filter Pills or Category Pills - scrollable with fade masks */}
                {isSuppliersEndpoint &&
                supplierCategories &&
                supplierCategories.length > 0 ? (
                    <CategoryPills categories={supplierCategories} />
                ) : (
                    <div className="min-w-0 flex-1 overflow-visible">
                        <div className="relative overflow-visible">
                            {/* Left fade overlay - only visible when scrolled */}
                            {showLeftFade && (
                                <div
                                    className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6"
                                    style={{
                                        background:
                                            'linear-gradient(to right, white, transparent)',
                                    }}
                                />
                            )}
                            {/* Right fade overlay - only visible when more content available */}
                            {showRightFade && (
                                <div
                                    className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6"
                                    style={{
                                        background:
                                            'linear-gradient(to left, white, transparent)',
                                    }}
                                />
                            )}
                            {/* Scroll container - badge space (-my-4 py-4) is INSIDE to prevent clipping.
                                Only show grab cursor when pills actually overflow; otherwise the
                                flex-1 strip makes empty space between Product library and Brands
                                look clickable. */}
                            <div
                                ref={
                                    scrollRef as React.RefObject<HTMLDivElement | null>
                                }
                                className={cn(
                                    '-my-4 scrollbar-hide flex items-center gap-3 overflow-x-auto py-4',
                                    isOverflowing &&
                                        'cursor-grab active:cursor-grabbing',
                                )}
                            >
                                {searchFilters && (
                                    <Filters
                                        searchFilters={searchFilters}
                                        sortOptions={sortOptions}
                                        displayMode={DisplayMode.Desktop}
                                        showSortBy={false}
                                        hideModalButton={true}
                                        showSelectedCountBadge={true}
                                        disableWrap={true}
                                        filterPillClassName="border-[#F2F2F2] bg-[#F2F2F2] text-[#323232] hover:border-gray-200 hover:bg-gray-200 px-5 py-2.5 tracking-tighter [&>svg]:size-3.5 [&>svg]:ml-2.5"
                                        scrollableClassName="gap-3"
                                        filterButtonClassName="px-5 py-2.5 tracking-tighter [&>svg]:size-4 [&>svg]:ml-2.5"
                                        barTrailingComponent={
                                            isSuppliersEndpoint ? undefined : (
                                                <FiltersModal
                                                    showSorting={false}
                                                />
                                            )
                                        }
                                        hideFacetFilters={true}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Products/Brands Toggle - always visible */}
                <div className="ml-auto flex shrink-0 items-center gap-3">
                    <div className="relative flex items-center gap-2 rounded-full bg-[#ECECEC] px-[0.45rem] py-[0.3rem]">
                        {/* Sliding highlight background */}
                        <div
                            aria-hidden
                            className={cn(
                                'absolute top-[0.3rem] left-[0.45rem] h-[calc(100%-0.6rem)] rounded-full bg-white shadow-md duration-300',
                                disableHighlightTransition
                                    ? ''
                                    : 'transition-transform',
                                localIsSuppliers
                                    ? 'translate-x-full'
                                    : 'translate-x-0',
                            )}
                            style={{
                                width: 'calc(50% - 0.45rem)',
                            }}
                        />

                        <RoccoButton
                            weight="normal"
                            clickAnimation="scale"
                            onClick={() => {
                                if (isSuppliersEndpoint) {
                                    setDisableHighlightTransition(true);
                                    setLocalIsSuppliers(false);
                                    requestAnimationFrame(() => {
                                        setTimeout(() => {
                                            onToggle();
                                            setDisableHighlightTransition(
                                                false,
                                            );
                                        }, 50);
                                    });
                                }
                            }}
                            className={cn(
                                'relative z-10 flex-1 rounded-full border-none bg-transparent px-8 py-2.5 hover:bg-transparent',
                                !isSuppliersEndpoint
                                    ? 'font-medium text-[#232323]'
                                    : 'font-normal text-[#232323] opacity-60',
                            )}
                        >
                            <span className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base">
                                Products
                            </span>
                        </RoccoButton>

                        <RoccoButton
                            weight="normal"
                            clickAnimation="scale"
                            onClick={() => {
                                if (!isSuppliersEndpoint) {
                                    setDisableHighlightTransition(true);
                                    setLocalIsSuppliers(true);
                                    requestAnimationFrame(() => {
                                        setTimeout(() => {
                                            onToggle();
                                            setDisableHighlightTransition(
                                                false,
                                            );
                                        }, 50);
                                    });
                                }
                            }}
                            className={cn(
                                'relative z-10 flex-1 rounded-full border-none bg-transparent px-8 py-2.5 hover:bg-transparent',
                                isSuppliersEndpoint
                                    ? 'font-medium text-[#232323]'
                                    : 'font-normal text-[#232323] opacity-60',
                            )}
                        >
                            <span className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base">
                                Brands
                            </span>
                        </RoccoButton>
                    </div>
                </div>
            </div>

            {/* Row 2: Breadcrumbs + Sort By */}
            <div className="relative">
                <div className="px-4 py-12 md:px-12">
                    <hr className="m-0 h-0 border-t border-[#232323] opacity-20" />
                    {breadcrumbs.length > 0 && (
                        <nav aria-label="Breadcrumb" className="m-0 pt-5">
                            <ol className="flex flex-wrap items-center">
                                {breadcrumbs.map((crumb, index) => (
                                    <li
                                        key={crumb.href}
                                        className="inline-flex items-center text-5.5 leading-normal font-medium tracking-[-0.22px] text-[#141414]"
                                    >
                                        {index > 0 && (
                                            <span
                                                className="mx-2"
                                                aria-hidden="true"
                                            >
                                                /
                                            </span>
                                        )}
                                        <Link
                                            to={crumb.href}
                                            state={{
                                                restoreScroll: true,
                                                scroll: false,
                                            }}
                                        >
                                            {crumb.label}
                                        </Link>
                                    </li>
                                ))}
                            </ol>
                        </nav>
                    )}
                </div>
                {/* Sort BY dropdown positioned on the right */}
                <div className="absolute top-1/2 right-4 -translate-y-1/2 md:right-12">
                    <SortDropdown
                        sortOptions={sortOptions}
                        updateSort={searchFilters.updateSort}
                    />
                </div>
            </div>
        </>
    );
};
